import { getCityLocationList } from '_services/accountsServices/cityLocationService';
import { RouteCustomDropdown, TripRoutingModal } from 'components'
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import toast from 'react-hot-toast';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FaCheckCircle, FaMapMarkerAlt, FaClock, FaUser, FaPhone } from 'react-icons/fa'
import {
    GoogleMap,
    Marker,
    InfoWindow,
    DirectionsService,
    DirectionsRenderer,
    Polyline,
} from "@react-google-maps/api";
import { useGoogleMaps } from "components/GoogleMapsProvider/GoogleMapsProvider";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import Loading from 'pages/Loading';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';

// Sortable wrapper component for dropdown items - minimal UI changes
const SortableDropdownItem = ({ id, children, disabled }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({
        id,
        disabled: disabled
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.8 : 1,
    };
    return (
        <div
            ref={setNodeRef}
            style={style}
            {...(!disabled ? { ...attributes, ...listeners } : {})}
            className={`${isDragging ? 'dragging' : ''}`}
            title={!disabled ? "Drag to reorder" : ""}
        >
            {children}
        </div>
    );
};

const RoutePanningModal = (props) => {
    const { fromLoction, toLoction, setFromLoction, setToLoction, setShow, closeModal } = props;
    const isLoaded = useGoogleMaps();
    const mapRef = useRef(null);
    const markersRef = useRef([]);
    const clustererRef = useRef(null);
    const [pickupPoints, setPickupPoints] = useState([]);
    const [dropPoints, setDropPoints] = useState([]);
    const [directionsResponse, setDirectionsResponse] = useState(null);

    const containerStyle = {
        width: "100%",
        height: "calc(100% - 84px)",
        minHeight: "422px"
    };
    // Set up sensors for drag and drop
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );


    useEffect(() => {
        setPickupPoints((prev) => {
            const modifyList = fromLoction.map((item, index) => ({
                ...item,
                _id: item.from_city ? `${item.from_city} ${item.from_city && item.from_state ? "," : ""} ${item.from_state}` : "",
                label: item.from_city ? `${item.from_city} ${item.from_city && item.from_state ? "," : ""} ${item.from_state}` : "",
                uniqueId: item.from_city ? `pickup_${index}_${item.from_city}_${item.from_state}` : "" // Stable unique ID
            }));
            return modifyList;
        });
        setDropPoints((prev => {
            const modifyList = toLoction.map((item, index) => ({
                ...item,
                _id: item.to_city ? `${item.to_city} ${item.to_city && item.to_state ? ", " : ""} ${item.to_state}` : "",
                label: item.to_city ? `${item.to_city} ${item.to_city && item.to_state ? ", " : ""} ${item.to_state}` : "",
                uniqueId: item.to_city ? `drop_${index}_${item.to_city}_${item.to_state}` : "" // Stable unique ID
            }));
            return modifyList;
        }));
    }, [fromLoction, toLoction]);

    // Shared initial city data for all dropdowns
    const [initialCityData, setInitialCityData] = useState({
        items: [],
        total: 0,
        isLoaded: false,
        isLoading: false
    });

    // Cache for search results to avoid duplicate API calls
    const searchCacheRef = useRef(new Map());

    // Load initial city data only once when modal opens
    useEffect(() => {
        const loadInitialCities = async () => {
            if (initialCityData.isLoading || initialCityData.isLoaded) return;

            setInitialCityData(prev => ({ ...prev, isLoading: true }));

            try {
                const res = await getCityLocationList({
                    countryCode: "IN",
                    stateCode: "",
                    offset: 1,
                    limit: 100, // Load more cities initially for better UX
                    search: "",
                });

                if (res?.responseCode === 200) {
                    const list = res?.data?.all_cities.map((c) => ({
                        ...c,
                        _id: `${c.name}, ${c.stateName}`,
                        label: `${c.name}, ${c.stateName}`
                    }));

                    setInitialCityData({
                        items: list,
                        total: res?.data?.total_record,
                        isLoaded: true,
                        isLoading: false
                    });
                } else {
                    toast.error(res?.responseMessage);
                    setInitialCityData({
                        items: [],
                        total: 0,
                        isLoaded: true,
                        isLoading: false
                    });
                }
            } catch (error) {
                toast.error('Failed to load cities');
                setInitialCityData({
                    items: [],
                    total: 0,
                    isLoaded: true,
                    isLoading: false
                });
            }
        };

        // Only load when modal is presumably open (component mounted)
        loadInitialCities();
    }, []); // Empty dependency - runs only once when component mounts

    // Fetch function for dropdowns - uses initial data or makes API calls for search/scroll
    const fetchFromCity = useCallback(async (search, page, limit) => {
        const searchTerm = search?.trim() || "";

        // If no search term and page 1, return initial loaded data
        if (!searchTerm && page === 1) {
            return {
                items: initialCityData.items,
                total: initialCityData.total,
            };
        }

        // Create cache key for search results
        const cacheKey = `${searchTerm}_${page}_${limit}`;

        // Return cached search results if available
        if (searchCacheRef.current.has(cacheKey)) {
            return searchCacheRef.current.get(cacheKey);
        }

        // Make API call for search or pagination
        try {
            const res = await getCityLocationList({
                countryCode: "IN",
                stateCode: "",
                offset: page,
                limit,
                search: searchTerm,
            });

            if (res?.responseCode === 200) {
                const list = res?.data?.all_cities.map((c) => ({
                    ...c,
                    _id: `${c.name}, ${c.stateName}`,
                    label: `${c.name}, ${c.stateName}`
                }));

                const result = {
                    items: list,
                    total: res?.data?.total_record,
                };

                // Cache the search result
                searchCacheRef.current.set(cacheKey, result);

                return result;
            } else {
                toast.error(res?.responseMessage);
                return { items: [], total: 0 };
            }
        } catch (error) {
            toast.error('Failed to fetch cities');
            return { items: [], total: 0 };
        }
    }, [initialCityData]);

    // Handle drag end for pickup points
    const handlePickupDragEnd = (event) => {
        const { active, over } = event;

        if (active.id !== over?.id) {
            setPickupPoints((items) => {
                const oldIndex = items.findIndex(item => item.uniqueId === active.id);
                const newIndex = items.findIndex(item => item.uniqueId === over.id);
                return arrayMove(items, oldIndex, newIndex);
            });
        }
    };

    // Handle drag end for drop points
    const handleDropDragEnd = (event) => {
        const { active, over } = event;

        if (active.id !== over?.id) {
            setDropPoints((items) => {
                const oldIndex = items.findIndex(item => item.uniqueId === active.id);
                const newIndex = items.findIndex(item => item.uniqueId === over.id);
                return arrayMove(items, oldIndex, newIndex);
            });
        }
    };


   const onSubmitTrip = () => {
   
    const sanitizedPickupPoints = pickupPoints.map(({ _id, uniqueId,label, ...rest }) => rest);
    const sanitizedDropPoints = dropPoints.map(({ _id, uniqueId, label,...rest }) => rest);

    setFromLoction(sanitizedPickupPoints);
    setToLoction(sanitizedDropPoints);
    closeModal();
    setShow(true);
};

    // Clear search cache when component unmounts
    useEffect(() => {
        return () => {
            searchCacheRef.current.clear();
        };
    }, []);

    const modifyProps = {
        ...props,
        onSubmit: onSubmitTrip,
    }


    //      // Combine all stops
    const allStops = useMemo(() => {
        const formatFromLabel = (point, icon) => ({
            ...point,
            label: `${icon} ${point.from_city}, ${point.from_state}`,
        });

        const formatToLabel = (point, icon) => ({
            ...point,
            label: `${icon} ${point.to_city}, ${point.to_state}`,
        });

        const formattedPickups = pickupPoints.map(point => formatFromLabel(point, "📤"));
        const formattedDrops = dropPoints.map(point => formatToLabel(point, "📥"));

        return [...formattedPickups, ...formattedDrops];
    }, [pickupPoints, dropPoints]);

    //       // Origin = first stop, Destination = last stop
    const origin = useMemo(
        () => new window.google.maps.LatLng(allStops[0]?.lat, allStops[0]?.long),
        [allStops]
    );
    const destination = useMemo(
        () =>
            new window.google.maps.LatLng(
                allStops[allStops.length - 1]?.lat,
                allStops[allStops.length - 1]?.long
            ),
        [allStops]
    );

    //   Waypoints: all intermediate stops
    const waypoints = useMemo(
        () =>
            allStops.slice(1, -1).map((stop) => ({
                location: new window.google.maps.LatLng(stop?.lat, stop?.long),
                stopover: true,
            })),
        [allStops]
    );


    const defaultCenter = useMemo(() => ({ lat: 20.5937, lng: 78.9629 }), []);
    const defaultZoom = 5;
    // Stable map options
    const mapOptions = useMemo(
        () => ({
            disableDefaultUI: false,
            zoomControl: true,
            streetViewControl: true,
            mapTypeControl: true,
            fullscreenControl: true,
        }),
        []
    );

    // Memoized DirectionsRenderer options
    const directionsOptions = useMemo(() => {
        return directionsResponse
            ? {
                directions: directionsResponse,
                polylineOptions: {
                    strokeColor: "#62c412",
                    strokeWeight: 6,
                    strokeOpacity: 0.9,
                },
                markerOptions: { visible: false }, // Hide default markers
            }
            : {};
    }, [directionsResponse]);

    useEffect(() => {
        if (!isLoaded) return;

        const calculateRoute = async () => {
            try {
                const directionsService = new window.google.maps.DirectionsService();
                const result = await directionsService.route({
                    origin,
                    destination,
                    waypoints,
                    travelMode: window.google.maps.TravelMode.DRIVING,
                    optimizeWaypoints: false,
                });

                setDirectionsResponse(result);
                //   setError("");

                // Fit map to all stops
                const bounds = new window.google.maps.LatLngBounds();
                allStops.forEach((stop) => {
                    bounds.extend(new window.google.maps.LatLng(stop.lat, stop.long));
                });
                if (mapRef.current) {
                    mapRef.current.fitBounds(bounds);
                }
            } catch (err) {
                //   setError("Error loading route: " + err.message);
                console.error(err);
            }
        };

        calculateRoute();
    }, [isLoaded, origin, destination, waypoints, allStops]);

    // Show loading state until initial data is ready
    if (!initialCityData.isLoaded) {
        return (
            <TripRoutingModal {...modifyProps}>
                <div className='__mapFlxr_bx'>
                    <div className='__lftsIde_bx'><div className="loaderBg" style={{ textAlign: "center",paddingRight:"5rem" }}><Loading /></div></div>
                    <div className='__rtsIde_bx'>
                        <div className='__rtsIde_main'>
                            <div style={{
                                textAlign: 'center',
                                padding: '50px 20px',
                                fontSize: '16px',
                                color: '#666'
                            }}>
                                {initialCityData.isLoading ? 'Loading cities...' : 'Preparing city data...'}
                            </div>
                        </div>
                        <div className='__rtsIde_footer'>
                            <button className='anime-btn' disabled>
                                Loading...
                            </button>
                        </div>
                    </div>
                </div>
            </TripRoutingModal>
        );
    }


    return (
        <>
            <TripRoutingModal {...modifyProps}>
                <div className='__mapFlxr_bx'>
                    <div className='__lftsIde_bx'>
                        {!isLoaded ? (
                            <div className="loaderBg" style={{ textAlign: "center" }}><Loading /></div>
                        ) : (
                            <GoogleMap
                                mapContainerStyle={{ width: "100%", height: "100%" }}
                                center={defaultCenter}
                                zoom={defaultZoom}
                                options={mapOptions}
                                onLoad={(map) => {
                                    mapRef.current = map;
                                }}
                            >
                                {/* Render Markers */}
                                {allStops.map((stop, index) => {
                                    let icon = null;
                                    let label = null;

                                    // First stop: Truck icon (origin)
                                    if (index === 0) {
                                        icon = {
                                            url: "https://img.icons8.com/color/48/000000/truck.png",
                                            scaledSize: new window.google.maps.Size(40, 40),
                                            origin: new window.google.maps.Point(0, 0),
                                            anchor: new window.google.maps.Point(20, 40),
                                        };
                                    }
                                    // Last stop: Warehouse icon (destination)
                                    else if (index === allStops.length - 1) {
                                        icon = {
                                            url: "https://img.icons8.com/fluency/48/000000/warehouse.png",
                                            scaledSize: new window.google.maps.Size(40, 40),
                                            origin: new window.google.maps.Point(0, 0),
                                            anchor: new window.google.maps.Point(20, 40),
                                        };
                                    }
                                    // Intermediate stops: Number label
                                    else {
                                        label = {
                                            text: `${index}`,
                                            fontSize: "14px",
                                            fontWeight: "bold",
                                            color: "white",
                                            backgroundColor: index < pickupPoints.length ? "#3366FF" : "#FF5722",
                                            borderRadius: "50%",
                                            padding: "0",
                                            textAlign: "center",
                                            width: "20px",
                                            height: "20px",
                                            border: "2px solid white",
                                        };
                                    }

                                    return (
                                        <Marker
                                            key={stop._id}
                                            position={{
                                                lat: parseFloat(stop.lat),
                                                lng: parseFloat(stop.long)
                                            }}
                                            icon={icon}
                                            label={label}
                                            title={stop.label}
                                        />
                                    );
                                })}

                                {/* Render Route */}
                                {directionsResponse && (
                                    <DirectionsRenderer options={directionsOptions} />
                                )}
                            </GoogleMap>


                        )}
                    </div>

                    <div className='__rtsIde_bx'>
                        <div className='__rtsIde_main'>
                            <div className='__mngL_bx'>
                                <div className='__mngInnr_bx'>
                                    <h4>Pickup Point</h4>

                                    <DndContext
                                        sensors={sensors}
                                        collisionDetection={closestCenter}
                                        onDragEnd={handlePickupDragEnd}
                                         modifiers={[restrictToVerticalAxis]}
                                    >
                                        <SortableContext
                                            items={pickupPoints.map(item => item.uniqueId)}
                                            strategy={verticalListSortingStrategy}
                                        >
                                            {pickupPoints.map((item, index) => (
                                                <SortableDropdownItem
                                                    key={item.uniqueId}
                                                    id={item.uniqueId}
                                                // disabled={fromLoction.length > 0} // Disable drag when fromLoction has data
                                                >
                                                    <RouteCustomDropdown
                                                        placeholder="Select first pickup city"
                                                        fetchData={fetchFromCity}
                                                        onChange={(data) => {
                                                            setPickupPoints(prev => {
                                                                const newPoints = [...prev];
                                                                const itemIndex = newPoints.findIndex(p => p.uniqueId === item.uniqueId);
                                                                if (itemIndex !== -1) {
                                                                    newPoints[itemIndex] = {
                                                                        ...data,
                                                                        uniqueId: item.uniqueId
                                                                    };
                                                                }
                                                                return newPoints;
                                                            });
                                                        }}
                                                        disabled={fromLoction.length > 0} // Keep original disable logic
                                                        selectedItem={item}
                                                        pickUpPoint={true}
                                                        label={`Pickup Location ${index +1}`}
                                                    />
                                                </SortableDropdownItem>
                                            ))}
                                        </SortableContext>
                                    </DndContext>
                                </div>

                                <div className='__mngInnr_bx'>
                                    <h4>Drop Point</h4>

                                    <DndContext
                                        sensors={sensors}
                                        collisionDetection={closestCenter}
                                        onDragEnd={handleDropDragEnd}
                                        modifiers={[restrictToVerticalAxis]}
                                    >
                                        <SortableContext
                                            items={dropPoints.map(item => item.uniqueId)}
                                            strategy={verticalListSortingStrategy}
                                        >
                                            {dropPoints.map((item, index) => (
                                                <SortableDropdownItem
                                                    key={item.uniqueId}
                                                    id={item.uniqueId}
                                                // disabled={toLoction.length > 0} // Disable drag when toLoction has data
                                                >
                                                    <RouteCustomDropdown
                                                        placeholder="Select first drop city"
                                                        fetchData={fetchFromCity}
                                                        onChange={(data) => {
                                                            setDropPoints(prev => {
                                                                const newPoints = [...prev];
                                                                const itemIndex = newPoints.findIndex(p => p.uniqueId === item.uniqueId);
                                                                if (itemIndex !== -1) {
                                                                    newPoints[itemIndex] = {
                                                                        ...data,
                                                                        uniqueId: item.uniqueId
                                                                    };
                                                                }
                                                                return newPoints;
                                                            });
                                                        }}
                                                        disabled={toLoction.length > 0} // Keep original disable logic
                                                        selectedItem={item}
                                                        dropPoint={true}
                                                        label={`Drop Location ${index +1}`}
                                                    />
                                                </SortableDropdownItem>
                                            ))}
                                        </SortableContext>
                                    </DndContext>
                                </div>
                            </div>
                        </div>

                        <div className='__rtsIde_footer'>
                            <button className='anime-btn' onClick={onSubmitTrip}>Create Trip</button>
                        </div>
                    </div>
                </div>
            </TripRoutingModal>

            <style jsx>{`
                .dragging {
                    z-index: 999;
                    cursor: grabbing !important;
                },
                     @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
            `}</style>
        </>
    )
}

export default RoutePanningModal